import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../../components/ui/sheet";

const STATUS_LABELS = {
  scheduled: { text: "Scheduled", cls: "border-[#E5E1D8] text-[#6E6E6E]" },
  open: { text: "Waiting", cls: "border-[#FF6A00] text-[#FF6A00]" },
  live: { text: "● LIVE", cls: "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" },
  final_window: { text: "Final Window", cls: "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" },
  ended: { text: "Ended", cls: "border-[#E5E1D8] text-[#6E6E6E]" },
};

export default function AdminDropOverview() {
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDropId, setSelectedDropId] = useState(null);

  const load = async () => {
    try {
      const r = await adminApi.get("/admin/drops/overview");
      setDrops(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000); // refresh every 5s for near-real-time
    return () => clearInterval(iv);
  }, []);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return drops;
    return drops.filter((d) => d.status === statusFilter);
  }, [drops, statusFilter]);

  const totals = useMemo(() => ({
    lives: drops.length,
    joins: drops.reduce((s, d) => s + (d.participants || 0), 0),
    bookings: drops.reduce((s, d) => s + (d.bookings || 0), 0),
    revenue: drops.reduce((s, d) => s + (d.revenue || 0), 0),
    peak: drops.reduce((m, d) => Math.max(m, d.peak_viewers || 0), 0),
  }), [drops]);

  return (
    <AdminShell>
      <div className="brand-tag">// 03</div>
      <h1 className="font-display text-5xl uppercase tracking-tight mt-1">Drop Overview</h1>
      <p className="mt-2 text-[#6E6E6E] max-w-2xl text-sm">
        Every drop, side by side. See bookings, revenue, live-viewer peaks and the pulse curve — the room-fill pattern across the entire session.
      </p>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-1 mt-8" data-testid="drop-overview-totals">
        <Totem label="Total Drops" value={totals.lives} />
        <Totem label="All-Time Joins" value={totals.joins.toLocaleString("en-IN")} />
        <Totem label="Bookings" value={totals.bookings.toLocaleString("en-IN")} />
        <Totem label="Revenue (₹)" value={totals.revenue.toLocaleString("en-IN", { maximumFractionDigits: 0 })} accent />
        <Totem label="Highest Peak" value={totals.peak.toLocaleString("en-IN")} />
      </div>

      {/* Status filter */}
      <div className="mt-8 flex flex-wrap gap-1" data-testid="drop-status-filter">
        {["all", "live", "final_window", "open", "scheduled", "ended"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-2 font-mono-ds text-[11px] uppercase tracking-widest border ${
              statusFilter === s
                ? "bg-[#FF6A00] border-[#FF6A00] text-[#161616] font-bold"
                : "border-[#E5E1D8] text-[#6E6E6E] hover:border-[#C4BEB0] hover:text-[#161616]"
            }`}
            data-testid={`filter-${s}`}
          >
            {s === "all" ? "All" : (STATUS_LABELS[s]?.text || s)}
            <span className="ml-2 opacity-70 tabular-nums">
              {s === "all" ? drops.length : drops.filter((d) => d.status === s).length}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="mt-6 border border-[#E5E1D8] bg-[#FFFFFF] overflow-x-auto" data-testid="drop-overview-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E1D8] font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
              <Th>Drop</Th>
              <Th>Scheduled</Th>
              <Th>Status</Th>
              <Th align="right">Joins</Th>
              <Th align="right">Peak</Th>
              <Th align="right">Bookings</Th>
              <Th align="right">Revenue</Th>
              <Th>Pulse</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="p-10 text-center font-mono-ds text-[#6E6E6E]">// Loading drops...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center font-mono-ds text-[#6E6E6E]">// No drops match this filter.</td></tr>
            )}
            {filtered.map((d) => (
              <DropRow key={d.live_id} drop={d} onPulseClick={() => setSelectedDropId(d.live_id)} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pulse drawer */}
      <Sheet open={!!selectedDropId} onOpenChange={(open) => { if (!open) setSelectedDropId(null); }}>
        <SheetContent side="right" className="bg-[#FFFFFF] border-l border-[#E5E1D8] text-[#161616] w-[92vw] max-w-3xl p-0 overflow-y-auto">
          <SheetTitle className="sr-only">Pulse detail</SheetTitle>
          <SheetDescription className="sr-only">Viewer count sampled every 2 seconds during the drop</SheetDescription>
          {selectedDropId && <PulseDetail liveId={selectedDropId} />}
        </SheetContent>
      </Sheet>
    </AdminShell>
  );
}

/* -------------------- Row -------------------- */
function DropRow({ drop, onPulseClick }) {
  const s = STATUS_LABELS[drop.status] || STATUS_LABELS.scheduled;
  const when = drop.scheduled_at ? new Date(drop.scheduled_at) : null;
  return (
    <tr className="border-b border-[#FFFFFF] hover:bg-[#FFFFFF]" data-testid={`drop-row-${drop.live_id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          {drop.product_image ? (
            <div className="w-10 h-10 bg-[#FFFFFF] overflow-hidden flex-shrink-0">
              <img src={drop.product_image} alt={drop.product_name} className="w-full h-full object-cover" />
            </div>
          ) : <div className="w-10 h-10 bg-[#FFFFFF] flex-shrink-0" />}
          <div className="min-w-0">
            <div className="font-display text-sm uppercase tracking-tight text-[#161616] truncate max-w-[220px]">
              {drop.title}
            </div>
            <div className="font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E] truncate max-w-[220px]">
              {drop.product_name || "—"} {drop.auto_promoted ? "· Vault" : ""}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap font-mono-ds text-xs text-[#6E6E6E]">
        {when ? when.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`font-mono-ds text-[10px] uppercase tracking-widest px-2 py-1 border ${s.cls}`}>
          {s.text}
        </span>
      </td>
      <td className="px-4 py-3 text-right font-mono-ds tabular-nums text-[#161616]" data-testid={`row-joins-${drop.live_id}`}>
        {(drop.participants || 0).toLocaleString("en-IN")}
      </td>
      <td className="px-4 py-3 text-right font-mono-ds tabular-nums text-[#FF6A00] font-bold" data-testid={`row-peak-${drop.live_id}`}>
        {(drop.peak_viewers || 0).toLocaleString("en-IN")}
      </td>
      <td className="px-4 py-3 text-right font-mono-ds tabular-nums text-[#161616]" data-testid={`row-bookings-${drop.live_id}`}>
        {(drop.bookings || 0).toLocaleString("en-IN")}
        {drop.paid_bookings != null && (
          <div className="text-[9px] text-[#6E6E6E] uppercase tracking-widest">{drop.paid_bookings} paid</div>
        )}
      </td>
      <td className="px-4 py-3 text-right font-mono-ds tabular-nums text-[#161616]" data-testid={`row-revenue-${drop.live_id}`}>
        ₹{(drop.revenue || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={onPulseClick}
          className="border border-[#E5E1D8] hover:border-[#FF6A00] hover:text-[#FF6A00] text-[#161616] font-mono-ds text-[10px] uppercase tracking-widest px-3 py-1.5 transition-colors"
          data-testid={`pulse-btn-${drop.live_id}`}
        >
          {drop.pulse_samples > 0 ? `View · ${drop.pulse_samples}pts` : "No data"}
        </button>
      </td>
    </tr>
  );
}

/* -------------------- Pulse detail drawer body -------------------- */
function PulseDetail({ liveId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi.get(`/admin/drops/${liveId}/pulse`).then((r) => { setData(r.data); }).finally(() => setLoading(false));
  }, [liveId]);

  if (loading || !data) {
    return <div className="p-8 font-mono-ds text-[#6E6E6E]">// Loading pulse timeline...</div>;
  }

  const samples = data.samples || [];
  return (
    <div className="p-6" data-testid="pulse-detail">
      <div className="brand-tag">◆ Pulse Meter</div>
      <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tighter mt-1 leading-tight break-words">
        {data.title}
      </h2>
      <p className="mt-2 text-[#6E6E6E] text-sm max-w-xl">
        Viewer count sampled every 2 seconds during the broadcast. The tallest bar is the moment the room was fullest — great for choosing your reveal timing next drop.
      </p>

      <div className="grid grid-cols-3 gap-1 mt-6">
        <MiniStat label="Peak Viewers" value={data.peak?.toLocaleString("en-IN") || "0"} accent />
        <MiniStat label="Final Count" value={(data.final_participants || 0).toLocaleString("en-IN")} />
        <MiniStat label="Samples" value={samples.length.toLocaleString("en-IN")} />
      </div>

      {samples.length === 0 ? (
        <div className="mt-8 border border-[#E5E1D8] bg-[#FFFFFF] p-10 text-center font-mono-ds text-[#6E6E6E]">
          // No pulse data yet — samples start once the host presses Start Live.
        </div>
      ) : (
        <PulseChart samples={samples} startedAt={data.started_at} endedAt={data.ended_at} peakAt={data.peak_at} crossings={data.milestone_crossings || []} />
      )}
    </div>
  );
}

function PulseChart({ samples, peakAt, crossings = [] }) {
  const maxV = Math.max(1, ...samples.map((s) => s.viewers || 0));
  const first = samples[0].bucket * 1000;
  const last = samples[samples.length - 1].bucket * 1000;
  const spanMs = Math.max(1000, last - first);
  const peakIdx = samples.reduce((best, s, i) => (s.viewers > samples[best].viewers ? i : best), 0);

  const posPct = (bucket) => {
    if (!bucket) return null;
    return (((bucket * 1000) - first) / spanMs) * 100;
  };

  return (
    <div className="mt-6 border border-[#E5E1D8] bg-[#FFFFFF] p-4" data-testid="pulse-chart">
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">// Viewers over time</div>
        <div className="font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
          Peak {samples[peakIdx].viewers.toLocaleString("en-IN")} @ {new Date(peakAt || samples[peakIdx].t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
        </div>
      </div>

      {/* Bar chart with milestone-crossing vertical guides */}
      <div className="relative h-52 flex items-end gap-[2px] overflow-hidden pb-4 pt-6" data-testid="pulse-bars">
        {samples.map((s, i) => {
          const h = ((s.viewers || 0) / maxV) * 100;
          const isPeak = i === peakIdx;
          return (
            <div
              key={`${s.bucket}-${i}`}
              className={`w-1.5 flex-shrink-0 transition-all ${isPeak ? "bg-[#FF6A00] shadow-[0_4px_12px_rgba(47,93,58,0.35)]" : "bg-[#C4BEB0] hover:bg-[#6E6E6E]"}`}
              style={{ height: `${Math.max(1, h)}%` }}
              title={`${s.viewers} viewers @ ${new Date(s.t).toLocaleTimeString()}`}
            />
          );
        })}

        {/* Milestone crossings — vertical guide + tiny price label */}
        {crossings.map((c) => {
          const pct = posPct(c.bucket);
          if (pct == null) return null;
          return (
            <div
              key={`ms-${c.index}`}
              className="absolute top-0 bottom-4 pointer-events-none"
              style={{ left: `${pct}%` }}
              data-testid={`milestone-marker-${c.index}`}
            >
              <div className="w-[2px] h-full bg-[#FF6A00] opacity-70" />
              <div className="absolute -top-1 -translate-x-1/2 bg-[#FF6A00] text-[#161616] font-mono-ds text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 leading-tight whitespace-nowrap">
                T{c.index + 1} · ₹{Number(c.price || 0).toLocaleString("en-IN")}
              </div>
            </div>
          );
        })}
      </div>

      {/* X axis: start / mid / end */}
      <div className="flex justify-between font-mono-ds text-[9px] uppercase tracking-widest text-[#6E6E6E] mt-1">
        <span>{fmtTime(first)}</span>
        <span>{fmtTime(first + spanMs / 2)}</span>
        <span>{fmtTime(last)}</span>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center flex-wrap gap-x-4 gap-y-2 font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E]">
        <span className="flex items-center gap-2"><span className="w-2 h-2 bg-[#C4BEB0]" /> Viewers per 2s</span>
        <span className="flex items-center gap-2"><span className="w-2 h-2 bg-[#FF6A00]" /> Peak</span>
        <span className="flex items-center gap-2"><span className="w-[2px] h-3 bg-[#FF6A00]" /> Tier unlocked</span>
      </div>

      {/* Sample table (compressed, top 8 minutes) */}
      <details className="mt-6">
        <summary className="cursor-pointer font-mono-ds text-[10px] uppercase tracking-widest text-[#6E6E6E] hover:text-[#FF6A00]">
          Show sample table ({samples.length} points)
        </summary>
        <div className="mt-3 max-h-52 overflow-y-auto border border-[#E5E1D8]">
          <table className="w-full text-xs font-mono-ds">
            <thead className="sticky top-0 bg-[#FFFFFF]">
              <tr className="border-b border-[#E5E1D8]">
                <th className="text-left px-3 py-2 text-[#6E6E6E] text-[10px] uppercase tracking-widest">Time</th>
                <th className="text-right px-3 py-2 text-[#6E6E6E] text-[10px] uppercase tracking-widest">Viewers</th>
              </tr>
            </thead>
            <tbody>
              {samples.map((s, i) => (
                <tr key={`row-${s.bucket}-${i}`} className={`border-b border-[#FFFFFF] ${i === peakIdx ? "bg-[#FF6A00]/10" : ""}`}>
                  <td className="px-3 py-1 text-[#6E6E6E] tabular-nums">{new Date(s.t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</td>
                  <td className={`px-3 py-1 text-right tabular-nums ${i === peakIdx ? "text-[#FF6A00] font-bold" : "text-[#161616]"}`}>{s.viewers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function fmtTime(ms) {
  return new Date(ms).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function Totem({ label, value, accent }) {
  return (
    <div
      className={`border ${accent ? "border-[#FF6A00] shadow-[0_4px_12px_rgba(17,17,17,0.06)]" : "border-[#E5E1D8]"} bg-[#FFFFFF] p-4`}
      data-testid={`totem-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
    >
      <div className="font-mono-ds text-[9px] uppercase tracking-[0.3em] text-[#6E6E6E]">{label}</div>
      <div className={`font-display text-2xl md:text-3xl mt-1 tabular-nums ${accent ? "text-[#FF6A00]" : "text-[#161616]"}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className={`border ${accent ? "border-[#FF6A00]" : "border-[#E5E1D8]"} bg-[#FFFFFF]/70 px-3 py-2`}>
      <div className="font-mono-ds text-[9px] uppercase tracking-[0.3em] text-[#6E6E6E]">{label}</div>
      <div className={`font-display text-xl mt-1 tabular-nums ${accent ? "text-[#FF6A00]" : "text-[#161616]"}`}>{value}</div>
    </div>
  );
}

function Th({ children, align }) {
  return (
    <th className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"} font-medium`}>
      {children}
    </th>
  );
}
